import { memo } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import type { DepositIncomingCard } from '@/supabase/deposit/types/deposit';

interface DepositRowChipProps {
  card: DepositIncomingCard;
  confirming: boolean; // 전역 confirm/reject 진행 중 → 모든 버튼 비활성
  inFlightAction?: 'confirm' | 'reject' | null; // 이 카드에서 진행 중인 액션 — 스피너를 그 버튼에만
  justArrived?: boolean;
  confirmed?: boolean; // 방금 확정됨 → 칩 자리에 '확정됨 · 되돌리기' 5초 인라인 표시
  onConfirm: (card: DepositIncomingCard) => void;
  onReject: (card: DepositIncomingCard) => void; // ✗ 무시 → /deposits 수동 큐로
  onUndo?: (card: DepositIncomingCard) => void; // 되돌리기 → 확정 취소(상태+다리학습 원복)
}

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;

function DepositRowChipBase({ card, confirming, inFlightAction = null, justArrived = false, confirmed = false, onConfirm, onReject, onUndo }: DepositRowChipProps) {
  // 확정 직후: 토스트 대신 칩 자리에 '확정됨 · 되돌리기'(5초, 하단 카운트다운 바 후 자동 소멸).
  if (confirmed) {
    return (
      <span
        role="status"
        onClick={(e) => e.stopPropagation()}
        className="deposit-chip-in relative inline-flex items-center gap-1.5 bg-green-600 text-white rounded-full pl-2.5 pr-1 py-0.5 overflow-hidden"
      >
        <Check className="w-3.5 h-3.5" strokeWidth={3} aria-hidden />
        <span className="text-[11px] font-semibold">확정됨</span>
        <button
          type="button"
          aria-label={`${card.nickname} ${card.depositorName} ${card.amount.toLocaleString('ko-KR')}원 입금 확정 되돌리기`}
          className="text-[11px] font-semibold underline decoration-white/60 underline-offset-2 px-1.5 min-h-[28px] rounded-full hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 transition-colors"
          onClick={(e) => { e.stopPropagation(); onUndo?.(card); }}
        >
          되돌리기
        </button>
        <span className="deposit-countdown absolute bottom-0 left-0 h-[2px] w-full bg-white/50" aria-hidden />
      </span>
    );
  }
  return (
    <span
      onClick={(e) => e.stopPropagation()}
      className={[
        'inline-flex items-center gap-0.5 bg-blue-500 text-white rounded-full pl-2 pr-1 py-0.5',
        justArrived ? 'deposit-chip-in' : '',
      ].join(' ')}
    >
      <span className="text-[11px] font-semibold tabular-nums mr-0.5">{won(card.amount)}</span>
      {/* ✗ 제외 — ✓ 왼쪽. 이 주문 입금이 아니면 주문 매칭에서 제외(/deposits '제외됨'으로, 주문 상태 무변경). */}
      <button
        type="button"
        aria-label={`주문 매칭에서 제외 — ${card.nickname} ${card.depositorName} ${card.amount.toLocaleString('ko-KR')}원`}
        className="inline-flex items-center justify-center min-h-[32px] min-w-[32px] rounded-full bg-white/0 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        disabled={confirming}
        onClick={(e) => { e.stopPropagation(); onReject(card); }}
      >
        {inFlightAction === 'reject' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5 text-red-400" strokeWidth={3} />}
      </button>
      <button
        type="button"
        aria-label={`입금 확인 — ${card.nickname} ${card.depositorName} ${card.amount.toLocaleString('ko-KR')}원`}
        className="inline-flex items-center justify-center min-h-[32px] min-w-[32px] rounded-full bg-white/0 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        disabled={confirming}
        onClick={(e) => { e.stopPropagation(); onConfirm(card); }}
      >
        {inFlightAction === 'confirm' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 text-green-400" strokeWidth={3} />}
      </button>
    </span>
  );
}

export const DepositRowChip = memo(DepositRowChipBase);
